/**
 * ============================================================================
 * Traceability 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义追溯模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * 
 * 📦 包含的功能：
 * - 正向追溯（原材料→成品）
 * - 反向追溯（成品→原材料）
 * - 全链路追溯
 * - 快速查询
 * 
 * @module api/traceability/factory
 * @generated Manual (AI-assisted)
 * @last-updated 2025-11-28
 * ============================================================================
 */

import type { IRequestClient } from '../core/client'
import { API_ROUTES } from './routes'
import type {
  ForwardTraceResult,
  BackwardTraceResult,
  FullTraceResult,
  LifecycleTraceResult,
} from '../../types/traceability'

export type TraceQueryType =
  | 'batch'
  | 'material'
  | 'material_batch'
  | 'internal_batch'
  | 'supplier_batch'
  | 'heat_no'
  | 'auto'

/**
 * 创建追溯模块 API
 * 
 * @param client - 请求客户端实例
 * @returns 追溯模块 API 方法集合
 * 
 * @example
 * ```typescript
 * const traceApi = createTraceabilityApi(requestClient)
 * 
 * // 正向追溯：原材料 → 成品
 * const result = await traceApi.traceForward('RM-20251118-001')
 * 
 * // 反向追溯：成品 → 原材料
 * const result = await traceApi.traceBackward('WO001-B001-20251118')
 * ```
 */
export function createTraceabilityApi(client: IRequestClient) {
  return {
    // =========================================================================
    // 追溯查询
    // =========================================================================

    /**
     * 正向追溯：根据原材料批次号查询生产了哪些成品
     * 
     * 使用场景：
     * - 某批原材料有质量问题，需要追溯影响范围
     * - 召回管理：查看该原材料被用于哪些产品
     * 
     * @param materialBatchNo - 原材料批次号
     * @returns Promise<ForwardTraceResult> 正向追溯结果
     * 
     * @example
     * ```typescript
     * const result = await traceApi.traceForward('RM-20251118-001')
     * console.log(result.productionBatches) // 使用该原材料的成品批次
     * ```
     */
    traceForward(materialBatchNo: string) {
      return client.get<ForwardTraceResult>(
        API_ROUTES.traceability.forward(materialBatchNo)
      )
    },

    /**
     * 反向追溯：根据成品批次号查询使用了哪些原材料
     * 
     * 使用场景：
     * - 客户投诉某批产品，需要追溯生产过程
     * - 质量追溯：定位问题工序和责任人
     * 
     * @param batchNo - 成品批次号
     * @returns Promise<BackwardTraceResult> 反向追溯结果
     * 
     * @example
     * ```typescript
     * const result = await traceApi.traceBackward('WO001-B001-20251118')
     * console.log(result.materials)     // 使用的原材料
     * console.log(result.workReports)   // 报工记录
     * console.log(result.salesOrder)    // 关联的销售订单
     * ```
     */
    traceBackward(batchNo: string) {
      return client.get<BackwardTraceResult>(
        API_ROUTES.traceability.backward(batchNo)
      )
    },

    /**
     * 全链路追溯：完整的正反向追溯
     * 
     * 使用场景：
     * - 需要完整追溯链（原材料→生产→销售）
     * - 审计场景：完整追溯某批次的全生命周期
     * 
     * @param batchNo - 批次号
     * @returns Promise<FullTraceResult> 全链路追溯结果
     */
    traceFull(batchNo: string) {
      return client.get<FullTraceResult>(
        API_ROUTES.traceability.full(batchNo)
      )
    },

    /**
     * 快速查询：自动判断追溯方向
     * 
     * 使用场景：
     * - 统一查询入口，系统自动判断是正向还是反向追溯
     * 
     * @param keyword - 查询关键字（生产批次、内部批次、供应商来料批次或炉号）
     * @param type - 类型：'batch' | 'material' | 'supplier_batch' | 'heat_no' | 'auto'
     * @returns Promise<ForwardTraceResult | BackwardTraceResult>
     */
    traceQuery(keyword: string, type: TraceQueryType = 'auto') {
      return client.get<ForwardTraceResult | BackwardTraceResult>(
        API_ROUTES.traceability.query,
        { params: { keyword, type } }
      )
    },

    /**
     * 生命周期追溯：完整的产品生命周期追溯
     * 
     * 使用场景：
     * - 需要查看产品从原材料采购到发货的完整时间线
     * - 审计场景：完整追溯某批次的全生命周期
     * - 包含：时间线、阶段汇总、多级工单树、工序链
     * 
     * @param batchNo - 批次号
     * @returns Promise<LifecycleTraceResult> 生命周期追溯结果
     * 
     * @example
     * ```typescript
     * const result = await traceApi.traceLifecycle('WO001-B001-20251118')
     * console.log(result.lifecycle.timeline) // 时间线事件
     * console.log(result.lifecycle.stages)   // 阶段汇总
     * console.log(result.workOrderTree)      // 多级工单树
     * console.log(result.operationChain)     // 完整工序链
     * console.log(result.customerInfo)       // 客户信息
     * ```
     */
    traceLifecycle(batchNo: string) {
      return client.get<LifecycleTraceResult>(
        API_ROUTES.traceability.lifecycle(batchNo)
      )
    },
  }
}
